// components/Tasks/index.tsx
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import {
  Shell,
  Header,
  Title,
  Count,
  Empty,
  List,
  Item,
  Row,
  Name,
  TaskId,
  Badge,
  Spinner,
  Meta,
  Job,
  XButton,
} from "./styles";
import { useTaskStore } from "store/task";
import { taskStatusAPI } from "api";

const Tasks = () => {
  const { tasks, removeTask, updateTaskState } = useTaskStore();

  const timerRef = useRef<any>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  // ✅ "새 작업 추가" 감지용
  const prevIdsKeyRef = useRef<string>("");

  const ids = useMemo(
    () => tasks.filter((t) => t.state === "pending").map((t) => t.id),
    [tasks]
  );

  const fetchStatuses = useCallback(
    async (idsToFetch: string[]) => {
      if (idsToFetch.length === 0) return;

      try {
        const results = await Promise.allSettled(
          idsToFetch.map(async (id) => {
            const res = await taskStatusAPI.getTaskStatus(id);
            const status = res?.data?.status ?? "PENDING";
            return { id, status };
          })
        );

        results.forEach((r, i) => {
          const id = idsToFetch[i];

          if (r.status === "fulfilled") {
            const s = r.value.status;
            if (s === "SUCCESS") updateTaskState(id, "success");
            else if (s === "ERROR" || s === "FAILURE")
              updateTaskState(id, "error");
            else updateTaskState(id, "pending");
          } else {
            updateTaskState(id, "error");
          }
        });
      } catch (e) {
        console.error("Task polling error:", e);
      }
    },
    [updateTaskState]
  );

  // ✅ polling 시작
  useEffect(() => {
    fetchStatuses(ids);

    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      fetchStatuses(ids);
    }, 3000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
    };
  }, [ids, fetchStatuses]); // ✅ fetchStatuses 포함!

  // ✅ 새 task 추가되면 스크롤 위로
  useLayoutEffect(() => {
    const el = listRef.current;
    if (!el) return;

    const idsKey = ids.slice().sort().join("|");
    const prevKey = prevIdsKeyRef.current;

    if (!prevKey) {
      prevIdsKeyRef.current = idsKey;
      return;
    }

    const prevCount = prevKey.split("|").filter(Boolean).length;
    const nextCount = idsKey.split("|").filter(Boolean).length;

    if (nextCount > prevCount) {
      el.scrollTo({ top: 0, behavior: "smooth" });
    }

    prevIdsKeyRef.current = idsKey;
  }, [ids]);

  const allCount = tasks.length;
  const doneCount = tasks.filter(
    (t) => t.state === "success" || t.state === "error"
  ).length;

  return (
    <Shell>
      <Header>
        <Title>작업 상태</Title>
        <Count>
          {allCount === 0
            ? "추가된 작업이 없습니다."
            : `${doneCount}/${allCount} 완료`}
        </Count>
      </Header>

      {allCount === 0 ? (
        <Empty>작업을 추가하면 여기에서 상태를 볼 수 있어요.</Empty>
      ) : (
        <List ref={listRef}>
          {[...tasks].reverse().map((t) => {
            const { id, name, job, state } = t;

            return (
              <Item key={id} $status={state.toUpperCase()}>
                <Row>
                  <Name title={name}>{name}</Name>
                  <TaskId title={id}>{id}</TaskId>

                  <Badge $status={state.toUpperCase()}>
                    {state === "pending" && <Spinner aria-hidden="true" />}
                    {state === "pending"
                      ? "진행중"
                      : state === "success"
                      ? "완료"
                      : "에러"}
                  </Badge>

                  <Meta>상태: {state}</Meta>

                  <XButton
                    onClick={() => removeTask(id)}
                    aria-label={`${name} 삭제`}
                    title="삭제"
                  />
                </Row>

                {job && <Job>Job: {job}</Job>}
              </Item>
            );
          })}
        </List>
      )}
    </Shell>
  );
};

export default Tasks;
